import { useCallback, useEffect, useMemo, useState } from 'react';

type UserPerformanceSummary = {
  weakAreas: string[];
  studyTime: number; // hours
  completedCourses: number;
  averageScore: number; // 0-100
};

export const useUserPerformanceSummary = (userId: string | undefined) => {
  const [summary, setSummary] = useState<UserPerformanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return null;
    setLoading(true);
    setError(null);
    try {
      const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');

      // Get all courses under the user's CourseProgress subcollection
      const coursesRef = collection(db, 'users', userId, 'CourseProgress');
      const coursesSnap = await getDocs(coursesRef);

      let totalStudyMinutes = 0;
      let completedCourses = 0;
      let allLessonScores: number[] = [];
      const lowScoreLessons: string[] = [];

      // For each course, load Lessons subcollection
      for (const courseDoc of coursesSnap.docs) {
        const courseId = courseDoc.id;
        const courseData: any = courseDoc.data();
        if (courseData?.completed) completedCourses += 1;

        const lessonsRef = collection(db, 'users', userId, 'CourseProgress', courseId, 'Lessons');
        const lessonsSnap = await getDocs(lessonsRef);

        lessonsSnap.forEach((l) => {
          const lp: any = l.data();
          if (typeof lp?.timeSpent === 'number') {
            totalStudyMinutes += lp.timeSpent;
          }
          if (typeof lp?.quizScore === 'number') {
            allLessonScores.push(lp.quizScore);
            if (lp.quizScore < 60) {
              // Use lesson id as placeholder for weak area label
              lowScoreLessons.push(l.id);
            }
          }
        });
      }

      const averageScore = allLessonScores.length
        ? Math.round(allLessonScores.reduce((a, b) => a + b, 0) / allLessonScores.length)
        : 0;

      // Derive up to 3 weak areas based on most frequent low-score lesson ids
      const frequency: Record<string, number> = {};
      for (const id of lowScoreLessons) frequency[id] = (frequency[id] || 0) + 1;
      const weakAreas = Object.entries(frequency)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([lessonId]) => lessonId);

      const computed: UserPerformanceSummary = {
        weakAreas,
        studyTime: Math.round((totalStudyMinutes / 60) * 10) / 10,
        completedCourses,
        averageScore,
      };

      setSummary(computed);
      return computed;
    } catch (e) {
      console.error('Failed to load user performance summary', e);
      setError('Failed to load performance');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  return { summary, loading, error, reload: load };
};

export type { UserPerformanceSummary };


